import React, { useState, useEffect } from 'react';
import { 
  QueueListIcon, 
  ClockIcon, 
  UserIcon,
  PlayIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { queueAPI, userAPI } from '../../api';

const PatientQueue = () => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadDoctors();
  }, []);

  useEffect(() => {
    if (selectedDoctor) {
      loadQueueData(selectedDoctor._id);
    }
  }, [selectedDoctor]);

  useEffect(() => {
    let interval;
    if (autoRefresh && selectedDoctor) {
      interval = setInterval(() => {
        loadQueueData(selectedDoctor._id);
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, selectedDoctor]);

  const loadDoctors = async () => {
    try {
      const response = await userAPI.getUsers({ role: 'Doctor' });
      setDoctors(response.data?.users || []);
      if (response.data?.users?.length > 0) {
        setSelectedDoctor(response.data.users[0]);
      }
    } catch (error) {
      console.error('Error loading doctors:', error);
    }
  };

  const loadQueueData = async (doctorId) => {
    try {
      setLoading(true);
      const response = await queueAPI.getQueueSummary(doctorId);
      setQueueData(response.data);
    } catch (error) {
      console.error('Error loading queue data:', error);
    } finally {
      setLoading(false)
    }
  };

  const handleMarkArrived = async (appointmentId) => {
    try {
      await queueAPI.markArrived(appointmentId);
      loadQueueData(selectedDoctor._id);
    } catch (error) {
      console.error('Error marking patient as arrived:', error);
    }
  };

  const handleStartService = async (appointmentId) => {
    try {
      await queueAPI.startService(appointmentId);
      loadQueueData(selectedDoctor._id);
    } catch (error) {
      console.error('Error starting service:', error);
    }
  };

  const handleCompleteService = async (appointmentId) => {
    try {
      await queueAPI.completeService(appointmentId);
      loadQueueData(selectedDoctor._id);
    } catch (error) {
      console.error('Error completing service:', error);
    }
  };

  const handleAutoJoin = async () => {
    try {
      await queueAPI.autoJoinAppointments(selectedDoctor._id);
      loadQueueData(selectedDoctor._id);
    } catch (error) {
      console.error('Error auto-joining appointments:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'bg-gray-100 text-gray-800';
      case 'In-Queue': return 'bg-blue-100 text-blue-800';
      case 'In-Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const QueueItem = ({ appointment, position, isCurrentPatient = false }) => (
    <div className={`
      p-4 rounded-lg border transition-all duration-200
      ${isCurrentPatient 
        ? 'border-green-500 bg-green-50 shadow-lg' 
        : 'border-gray-200 bg-white hover:shadow-md'
      }
    `}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Position Number */}
          <div className={`
            w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
            ${isCurrentPatient ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}
          `}>
            {position}
          </div>
          
          {/* Patient Info */}
          <div>
            <h4 className="font-semibold text-gray-900">
              {appointment.patient?.firstName} {appointment.patient?.lastName}
            </h4>
            <p className="text-sm text-gray-500">
              {appointment.appointmentTime} • {appointment.patient?.mobile}
            </p>
            <p className="text-xs text-gray-400">
              Complaint: {appointment.complaint || 'Not specified'}
            </p>
          </div>
        </div>
        
        {/* Status and Actions */}
        <div className="flex items-center space-x-3">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(appointment.status)}`}>
            {appointment.status}
          </span>
          
          <div className="flex space-x-1">
            {appointment.status === 'Scheduled' && (
              <button
                onClick={() => handleMarkArrived(appointment._id)}
                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                title="Mark as Arrived"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
            )}
            
            {appointment.status === 'In-Queue' && !isCurrentPatient && (
              <button
                onClick={() => handleStartService(appointment._id)}
                className="p-1 text-green-600 hover:bg-green-100 rounded"
                title="Start Service"
              >
                <PlayIcon className="w-4 h-4" />
              </button>
            )}
            
            {appointment.status === 'In-Progress' && (
              <button
                onClick={() => handleCompleteService(appointment._id)}
                className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                title="Complete Service"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {isCurrentPatient && (
        <div className="mt-3 p-3 bg-green-100 rounded-lg">
          <p className="text-sm font-medium text-green-800">Currently Being Served</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Queue Management</h1>
          <p className="text-gray-600">Monitor and manage patient queues</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-600">Auto-refresh</span>
          </label>
          
          <button
            onClick={handleAutoJoin}
            disabled={!selectedDoctor}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg"
          >
            Auto-Join Ready Patients
          </button>
        </div>
      </div>

      {/* Doctor Selection */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Doctor</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {doctors.map((doctor) => (
            <button
              key={doctor._id}
              onClick={() => setSelectedDoctor(doctor)}
              className={`
                p-4 rounded-lg border text-left transition-all duration-200
                ${selectedDoctor?._id === doctor._id 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }
              `}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h4 className="font-medium text-gray-900">{doctor.name}</h4>
                  <p className="text-sm text-gray-500">{doctor.specialization || 'General'}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Queue Summary */}
      {queueData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center">
              <QueueListIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {queueData.queueSummary?.totalInQueue || 0}
                </h3>
                <p className="text-sm text-gray-500">Total in Queue</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center">
              <ClockIcon className="w-8 h-8 text-orange-600" />
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {queueData.queueSummary?.waiting || 0}
                </h3>
                <p className="text-sm text-gray-500">Waiting</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center">
              <PlayIcon className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {queueData.queueSummary?.inProgress || 0}
                </h3>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center">
              <CheckIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {queueData.queueSummary?.completed || 0}
                </h3>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Queue */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : queueData?.queue?.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Current Queue</h3>
          <div className="space-y-4">
            {queueData.queue.map((appointment, index) => (
              <QueueItem
                key={appointment._id}
                appointment={appointment}
                position={index + 1}
                isCurrentPatient={appointment.status === 'In-Progress'}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
          <QueueListIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Patients in Queue</h3>
          <p className="text-gray-500">
            {selectedDoctor ? 'No patients are currently waiting for this doctor.' : 'Select a doctor to view their queue.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PatientQueue;